import doRequest from "../helpers";
import { getToken } from "../auth/tokenAuth";
import { getUserId } from "services/storage/general";
import { getTextByRangeMinutes } from "components/CustomTimePicker/utils";
import {
	getFullExtendedDateByTimestamp,
	getTodayWithoutHourInMillis,
} from "components/CustomDatePicker/utils/helpers";
import helpers, { toEventItem, getBodyToUpdateRequestStep2 } from "./helpers";

const CERTIFICATION_ERROR_SCOPE_BY_CODE = {
	CERTIFICACION_CAMPUS_ORGANIZADOR_REQUERIDO: "campus",
	CERTIFICACION_CAMPUS_REQUERIDO: "campus",
	CERTIFICACION_CAMPUS_NO_COINCIDE: "campus",
	CERTIFICACION_CANTIDAD_FIRMAS_INVALIDA: "signatureCount",
	CERTIFICACION_CANTIDAD_FIRMAS_NO_COINCIDE: "signatureCount",
	CERTIFICACION_FIRMAS_INCOMPLETAS: "signatures",
	CERTIFICACION_TIPOS_DUPLICADOS: "duplicateTypes",
	CERTIFICACION_TIPO_FIRMA_INVALIDO: "signatures",
	CERTIFICACION_DOCENTE_PONENTE_REQUERIDO: "teacherSpeaker",
	CERTIFICACION_DOCENTE_SIN_FIRMA: "teacherSpeaker",
	CERTIFICACION_DOCENTE_FIRMA_AMBIGUA: "teacherSpeaker",
	CERTIFICACION_FIRMA_INSTITUCIONAL_NO_VIGENTE: "institutionalValidity",
	CERTIFICACION_FIRMA_INSTITUCIONAL_CAMPUS_INVALIDO: "institutionalValidity",
	CERTIFICACION_FIRMA_INSTITUCIONAL_CAMPUS_NO_COINCIDE: "institutionalValidity",
};

const CERTIFICATION_ERROR_TEXT_BY_CODE = {
	CERTIFICACION_CAMPUS_ORGANIZADOR_REQUERIDO:
		"Completa primero el campus organizador para configurar la generación automática de constancias.",
	CERTIFICACION_CAMPUS_REQUERIDO:
		"Debes definir un campus válido para la generación automática de constancias.",
	CERTIFICACION_CAMPUS_NO_COINCIDE:
		"La generación automática de constancias debe usar el mismo campus organizador del evento.",
	CERTIFICACION_CANTIDAD_FIRMAS_INVALIDA:
		"La generación automática de constancias solo permite entre 1 y 3 firmas.",
	CERTIFICACION_CANTIDAD_FIRMAS_NO_COINCIDE:
		"La cantidad de firmas configurada no coincide con los slots completados.",
	CERTIFICACION_FIRMAS_INCOMPLETAS:
		"Completa cada slot requerido antes de guardar la configuración de constancias.",
	CERTIFICACION_TIPOS_DUPLICADOS:
		"No puedes repetir el mismo tipo de firma dentro de la misma configuración de constancias.",
	CERTIFICACION_TIPO_FIRMA_INVALIDO:
		"Existe al menos un tipo de firma inválido en la generación automática de constancias.",
	CERTIFICACION_DOCENTE_PONENTE_REQUERIDO:
		"Si seleccionas DOCENTE / PONENTE, primero debes completar ese dato en 'Organizado por'.",
	CERTIFICACION_DOCENTE_SIN_FIRMA:
		"El docente / ponente debe tener una firma activa y vigente para el campus organizador y la fecha fin del evento.",
	CERTIFICACION_DOCENTE_FIRMA_AMBIGUA:
		"Existe más de una firma válida para el docente / ponente del evento en el campus organizador. Revisa la configuración en el administrador de firmas.",
	CERTIFICACION_FIRMA_INSTITUCIONAL_NO_VIGENTE:
		"No existe una firma institucional vigente para el campus organizador y la fecha fin del evento.",
	CERTIFICACION_FIRMA_INSTITUCIONAL_CAMPUS_INVALIDO:
		"No existe una firma institucional válida para el campus organizador seleccionado.",
	CERTIFICACION_FIRMA_INSTITUCIONAL_CAMPUS_NO_COINCIDE:
		"La firma institucional seleccionada no corresponde al campus organizador del evento.",
};

const parseCertificationError = message => {
	if (typeof message !== "string" || !message.startsWith("CERTIFICACION_"))
		return null;
	return {
		code: message,
		scope: CERTIFICATION_ERROR_SCOPE_BY_CODE[message] || "general",
		text:
			CERTIFICATION_ERROR_TEXT_BY_CODE[message] ||
			"No pudimos validar la generación automática de constancias. Revisa la configuración e inténtalo nuevamente.",
	};
};

//#region Evento CRUD
export const requestGetEventDrafts = async () => {
	const [result, err] = await doRequest(
		"/EventoCulturalBorrador",
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data.evento;
};
export const requestGetEventListAdmin = async ({
	filterType,
	page,
	date,
	title,
	organizerId,
}) => {
	const [result, err] = await doRequest(
		"/LeerEventoVisualizacionMod",
		"POST",
		{
			tipoFiltro: filterType,
			fechaBuscar: date,
			pagina: page,
			titulo: title,
			idOrganizador: organizerId,
			codigoUsuario: getUserId(),
		},
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	const { listaVisualizacionEventos, paraSincronizar } = data.data;
	return {
		eventList: listaVisualizacionEventos?.map(toEventItem) || null,
		toSynchronize: paraSincronizar || false,
	};
};
export const requestGetEventListTeacher = async ({
	page,
	date,
	title,
	organizerId,
}) => {
	const [result, err] = await doRequest(
		"/LeerEventoVisualizacionDocenteMod",
		"POST",
		{
			codigoDocente: getUserId(),
			pagina: page,
			fechaBuscar: date,
			titulo: title,
			idOrganizador: organizerId,
		},
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data.listaVisualizacionEventos.map(toEventItem);
};
export const requestGetQuantityEvents = async ({
	date,
	title,
	organizerId,
}) => {
	const [result, err] = await doRequest(
		"/CantidadListaEventoVisualizacionMod",
		"POST",
		{
			codigoUsuario: getUserId(),
			fechaBuscar: date,
			titulo: title,
			idOrganizador: organizerId,
		},
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	const {
		General: general,
		Pasados: past,
		Borrador: draft,
		Cancelados: canceled,
		NoProcesados: unprocessed,
	} = data.data.cantidadesEvento;
	return { 
		general, 
		past, 
		draft, 
		canceled, 
		unprocessed 
	};
};
export const requestGetEventById = async eventId => {
	const [result, err] = await doRequest(
		`/EventoCultural/Leer/${eventId}`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return helpers.parseToEventForm(data.data.evento);
};
//#region Reprogramación para varios Días
export const requestGetEventDaysByEventId = async (eventId, isAdmin) => {
	const [result, err] = await doRequest(
		`/EventoCultural/DiasEvento/${eventId}/${isAdmin}`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	const today = getTodayWithoutHourInMillis();
	return data.data.leerEvento?.map(
		({
			idEventoCulturalFecha: id,
			fecha,
			horaInicio,
			horaFin,
			programacion: { lugar, zoom },
			hoy: isToday,
			puedeTomarAsistencia: canTakeAttendance,
		}) => {
			const time = {
				value: {
					start: horaInicio,
					end: horaFin,
				},
				formatted: "",
			};
			time.formatted = getTextByRangeMinutes({ ...time.value }, "a");
			return {
				id,
				date: {
					value: fecha,
					formatted: getFullExtendedDateByTimestamp(fecha, today),
				},
				time: time,
				programming: {
					place: lugar && {
						campus: lugar.campus,
						address: lugar.direccion,
					},
					zoom,
				},
				canTakeAttendance,
				isToday,
				isPast: fecha < today,
			};
		}
	);
};
export const requestDeleteDateEventById = async id => {
	const [result, err] = await doRequest(
		"/EventoCultural/BorrarFecha",
		"POST",
		{
			idEventoCulturalFecha: id,
			codigoUsuario: getUserId(),
		},
		getToken()
	);
	if (err) return false;
	const { data } = result;
	if (data?.state !== 1) return false;
	return data.data.exito;
};
export const requestUpdateDateEventById = async ({
	id: idEventoCulturalFecha,
	date: fecha,
	time: { start: horaInicio, end: horaFin },
}) => {
	const body = {
		idEventoCulturalFecha,
		codigoUsuario: getUserId(),
		fecha,
		horaInicio,
		horaFin,
	};
	const [result, err] = await doRequest(
		"/Evento/EditarFechaHoraEvento",
		"POST",
		body,
		getToken()
	);
	if (err) return { success: false };
	const { data: payload } = result;
	if (payload?.state === 1) 
		return { success: true };
	const { data } = payload;
	return {
		success: data?.exito,
		message: data?.mensaje,
	};
};
// Lista de horarios con cruces
//#endregion
export const requestCreateEvent = async form => {
	const body = {
		codigoUsuario: getUserId(),
		...helpers.getBodyToAddOrUpdateRequestStep1(form),
	};

	const [result, err] = await doRequest(
		"/Evento/CargaManual/Crear",
		"POST",
		body,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data.idEvento;
};
export const requestUpdateEventById = async (
	eventId,
	form,
	step,
	ignoreDurationFields
) => {
	let body = {
		codigoUsuario: getUserId(),
		idEvento: eventId,
	};
	if (step === "step1") {
		body = {
			...body,
			...helpers.getBodyToAddOrUpdateRequestStep1(
				form,
				ignoreDurationFields
			),
		};
	} else if (step === "step2") {
		body = {
			...body,
			...getBodyToUpdateRequestStep2(form, {
				includeAutoCertification: true,
			}),
		};
	}
	const [result, err] = await doRequest(
		"/Evento/CargaManual/Editar",
		"POST",
		body,
		getToken()
	);
	if (err) return { 
		success: false, 
		message: null,
		schedulesWithCrossings: null,
		certificationError: null,
	};
	const { data: payload } = result;
	if (payload?.state === 1) 
		return { 
			success: true, 
			message: null,
			schedulesWithCrossings: null,
			certificationError: null,
		};
	const { data } = payload;
	const certificationError = parseCertificationError(data?.mensaje);
	return {
		success: data?.exito,
		message: data?.mensaje,
		schedulesWithCrossings:
			data?.listaCruces?.map(({ fechaHora }) => fechaHora) || null,
		certificationError,
	};
};
export const requestDeleteEventById = async eventId => {
	const [result, err] = await doRequest(
		`/Evento/CargaManual/Cancelar`,
		"POST",
		{
			idEvento: eventId,
			codigoUsuario: getUserId(),
		},
		getToken()
	);
	if (err) return false;
	const { data } = result;
	if (![0, 1].includes(data?.state))
		return {
			message: null,
			success: false,
		};
	return {
		message: data.data.mensaje,
		success: data.data.exito,
	};
};
export const requestFinalizeEventDraft = async eventId => {
	const [result, err] = await doRequest(
		"/Evento/CargaManual/Finalizar",
		"POST",
		{
			idEvento: eventId,
		},
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (![0, 1].includes(data?.state)) return null;
	return data.data?.mensaje || null;
};
export const requestEventExists = async eventId => {
	if (!eventId) return false;
	const [result, err] = await doRequest(
		`/EventoCulturalExiste/${eventId}`,
		"GET",
		null,
		getToken()
	);
	if (err) return false;
	const { data } = result;
	if (data?.state !== 1) return false;
	return data.data.evento;
};
export const requestCanUpdateEventDuration = async eventId => {
	if (!eventId) return false;
	const [result, err] = await doRequest(
		`/Evento/DuracionPuedeEditarse/${eventId}`,
		"GET",
		null,
		getToken()
	);
	if (err) return false;
	const { data } = result;
	if (data?.state !== 1) return false;
	return data.data.puedeEditarse;
};
export const requestValidateFileForCreateEventMassiveLoading = async fileBase64 => {
	const [result, err] = await doRequest(
		"/EventoCargaMasiva/ValidarArchivo",
		"POST",
		{ archivoCargaMasiva: fileBase64 },
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (![0, 1].includes(data?.state)) return null;
	return {
		rowCountFile: data.data.cantidadFilas,
		errorStack: data.data.pilaErrores.map(({ fila, mensaje }) => {
			return { row: fila, message: mensaje };
		}),
	};
};
export const requestCreateEventMassiveLoading = async fileBase64 => {
	const [result, err] = await doRequest(
		"/EventoCargaMasiva/Crear",
		"POST",
		{
			codigoUsuario: getUserId(),
			archivoCargaMasiva: fileBase64,
		},
		getToken()
	);
	if (err) return null;
	const { data } = result.data;
	if (data.exito)
		return {
			success: true,
		};
	const { fila, mensaje } = data;
	if (fila || mensaje)
		return {
			row: fila || 0,
			message: mensaje || null,
		};
	const { exito, cantERROR, cantOK, pilaErrores } = data;
	return {
		success: exito,
		quantities: {
			ok: cantOK || 0,
			error: cantERROR || 0,
		},
		errorStack:
			pilaErrores?.map(({ fila, codDocente, mensaje }) => ({
				row: fila,
				teacherId: codDocente,
				message: mensaje,
			})) || [],
	};
};
export const requestSyncUnprocessedEvent = async () => {
	const [result, err] = await doRequest(
		"/EventoCargaMasiva/Sincronizar",
		"POST",
		{ codigoUsuario: getUserId() },
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state === 0)
		return {
			message: data.data.mensaje,
		};
	if (data?.state !== 1) return null;
	const { Procesados, NoProcesados, Pendientes } = data.data;
	return {
		processed: Procesados,
		unprocessed: NoProcesados,
		pending: Pendientes,
	};
};
//#endregion
//#region Usuario
export const requestUserHasEvents = async (userCode, isTeacherUser) => {
	const [result, err] = await doRequest(
		"/UsuarioPorEventoCultural",
		"POST",
		[userCode, isTeacherUser],
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data.existe;
};
//#endregion
//#region Waitlist
export const requestGetWaitlistReport = async() => {
	const [result, err] = await doRequest(
		`/Evento/ReporteListaEspera/0`,//0 por default para que considere el periodo actual
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	const { archivoBase64 } = data.data;
	return archivoBase64;
};
//#endregion

export const requestGetStudentsEnrolledEvent = async eventId => {
	const [result, err] = await doRequest(
		`/EventoCultural/ReservaAlumnosInscritos/${eventId}`,
		"GET",
		null,
		getToken()
	);
	if (err) {

		return null
	};
	const { data } = result;
	if (data?.state !== 1) return null;
	const { listaAlumnos } = data.data;
	return listaAlumnos.map(
		({
			codigoAlumno: id,
			idReserva: reservationId,
			nombres: name,
			asistencia: attendance,
			fechaInscripcion: inscriptionDate
		}) => {
			return {
				reservationId,
				id,
				name,
				attendance,
				inscriptionDate
			};
		}
	);
};

export const requestGetProfessionalTools = async () => {
	const [result, err] = await doRequest(
		`/EventoCultural/HerramientasProfesionales`,
		"GET",
		null,
		getToken()
	);
	if (err) {
		return null
	};
	const { data } = result;
	if (data?.state !== 1) return null;
	const { listaEventos } = data.data;
	return listaEventos.map( evento => {

		return {
			idEvento: evento.idEvento,
			titulo: evento.titulo,
			seccion: evento.seccion,
			traspaso: evento.traslado
		};
	});
};

const buildQueryString = params => {
	const searchParams = new URLSearchParams();
	Object.entries(params || {}).forEach(([key, value]) => {
		if (value === null || typeof value === "undefined" || value === "")
			return;
		searchParams.append(key, String(value));
	});
	const query = searchParams.toString();
	return query ? `?${query}` : "";
};

export const requestGetPastEventCertificationSummary = async eventId => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/Resumen`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestGetPastEventCertificationConfig = async eventId => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/Configuracion`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestGetPastEventCertificationEditPermission = async eventId => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/PuedeEditar`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestGetPastEventCertificationStudents = async (
	eventId,
	{ status = "", search = "", page = 1, pageSize = 20 } = {}
) => {
	const query = buildQueryString({ status, search, page, pageSize });
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/Alumnos${query}`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestGetPastEventCertificationFile = async (
	eventId,
	studentId
) => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/Alumnos/${studentId}/Archivo`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestPastEventCertificationGlobalReprocess = async eventId => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/ReprocesoGlobal`,
		"POST",
		{ codigoUsuario: getUserId() },
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestPastEventCertificationStudentReprocess = async (
	eventId,
	studentId
) => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/Alumnos/${studentId}/Reproceso`,
		"POST",
		{ codigoUsuario: getUserId() },
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestGetPastEventCertificationBatches = async eventId => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/Lotes`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestGetPastEventCertificationBatch = async (eventId, batchId) => {
	const [result, err] = await doRequest(
		`/Evento/${eventId}/Certificacion/Lotes/${batchId}`,
		"GET",
		null,
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data;
};

export const requestTranslateStudents = async (IdEventoOrigen, IdEventoDestino, AlumnosTraspaso) => {
	const [result, err] = await doRequest(
		"/EventoCultural/TraspasoAlumnos",
		"POST",
		{
			IdEventoOrigen,
			IdEventoDestino,
			AlumnosTraspaso
		},
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data.existe;
};

export const requestGetDurations = async(eventId) => {
	const [result, err] = await doRequest(
		`/EventoCultural/DuracionPorIdEvento/${eventId}`,
		"GET",
		null,
		getToken()
	);
	if (err) {
		return null
	};
	const { data } = result;
	if (data?.state !== 1) return null;
	const { FechaFin, Duraciones} = data.data;

	return {
		fechaFin: FechaFin,
		duraciones: Duraciones.map( duracion => {
			return {
				idDuracion: duracion.idDuracion,
				horaInicio: duracion.horaInicio,
				horaFin: duracion.horaFin,
				idDia: duracion.idDia,
			};
		})
	};
}

export const requestUpdateDuration = async({
	idDuracion,
	fecha,
	idDia,
	horaInicio,
	horaFin
}) => {
	const [result, err] = await doRequest(
		"/Evento/EditarDuracionEvento",
		"POST",
		{
			idDuracion,
			"codigoUsuario": getUserId(),
			fecha,
			idDia,
			horaInicio,
			horaFin
		},
		getToken()
	);
	if (err) return null;
	const { data } = result;
	if (data?.state !== 1) return null;
	return data.data.exito;
}
